import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { selectAtom } from 'jotai/utils';
import { useEffect, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { OpenPosition, openPositionsBunja } from '@flipster/r230522/original/position';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { IPositionLineAdapter } from '../../../lib/charting_library';
import { ChartStyles } from '../../misc/constants';
import { useTradingViewDataStorage } from '../../misc/data-manager';
import { symbolNameToInfo } from '../../misc/helper';
import TradingView, { TvEventHandler } from '../../trading-view';
import { useTvHeaderContext } from '../index';

const positionsBunja = bunja(() => {
  const { openPositionsAtom } = bunja.use(openPositionsBunja);

  const symbolPositionAtomFamily = atomFamily((symbol: string) =>
    selectAtom(
      openPositionsAtom,
      (openPositions) => openPositions?.[symbol],
      // tpOrders, slOrders 는 positions 라인 드로잉에 불필요하므로 비교 제외
      // markPrice, size 등 가격 틱마다 바뀌는 필드도 무시
      (a, b) => {
        if (a === b) return true;
        if (a?.length !== b?.length) return false;
        return a.every((itemA, i) => {
          const itemB = b[i];
          return (
            itemA.positionId === itemB.positionId &&
            itemA.side === itemB.side &&
            itemA.quantity === itemB.quantity &&
            itemA.avgEntryPrice === itemB.avgEntryPrice &&
            itemA.unrealizedPnl === itemB.unrealizedPnl
          );
        });
      },
    ),
  );

  return { symbolPositionAtomFamily };
});

interface DrawOptions {
  pnl: string;
  qty: string;
  price: number;
}

export const PositionsPlugin = () => {
  const tv = useTvHeaderContext();
  const lineHelperRef = useRef<PositionLineHelper>(null);
  const [checked, setChecked] = useTradingViewDataStorage({ key: 'positionsVisible' });
  const [symbol, setSymbol] = useState<string>('');
  const { symbolPositionAtomFamily } = useBunja(positionsBunja);
  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const symbolPositions = useAtomValue(symbolPositionAtomFamily(symbolWithoutPriceType));

  useEffect(() => {
    if (!lineHelperRef.current) lineHelperRef.current = new PositionLineHelper(tv);
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      lineHelperRef.current?.clean('long');
      lineHelperRef.current?.clean('short');
      setSymbol(newSymbol);
    };
    tv.on('onFirstDataLoaded', handler);
    return () => tv.off('onFirstDataLoaded', handler);
  }, [tv]);

  useEffect(() => {
    if (!checked.value || !symbolPositions || symbolPositions.length === 0) {
      lineHelperRef.current?.clean('long');
      lineHelperRef.current?.clean('short');
      return;
    }
    const lineValues = calculatePositions(symbolPositions);
    (['long', 'short'] as const).forEach((side) => {
      const sideLineValue = lineValues[side];
      if (sideLineValue.price > 0) lineHelperRef.current?.draw(symbol, side, sideLineValue);
      else lineHelperRef.current?.clean(side);
    });
  }, [checked.value, symbol, symbolPositions]);

  useEffect(() => {
    return () => {
      lineHelperRef.current?.clean('long');
      lineHelperRef.current?.clean('short');
    };
  }, []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'Positions',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r250401.Positions' />
    </Checkbox>
  );
};

const PositionLineMapper = {
  long: {
    prefix: 'Long P&L ',
    color: ChartStyles.Color.Up,
  },
  short: {
    prefix: 'Short P&L ',
    color: ChartStyles.Color.Down,
  },
};

class PositionLineHelper {
  #lines: Record<'long' | 'short', IPositionLineAdapter | null> = {
    long: null,
    short: null,
  };

  constructor(private readonly tv: TradingView) {}

  draw(symbol: string, side: 'long' | 'short', options: DrawOptions) {
    try {
      if (!this.tv.enabled || !this.tv.activeChart) return;
      if (this.tv.symbol !== symbol) return;
      const lineInfo = PositionLineMapper[side];
      this.#lines[side] ??= this.tv
        .createPositionLine()
        .setLineColor(lineInfo.color)
        .setBodyTextColor(ChartStyles.Color.White)
        .setBodyBackgroundColor(lineInfo.color)
        .setBodyBorderColor(lineInfo.color)
        .setQuantityTextColor(ChartStyles.Color.White)
        .setQuantityBackgroundColor(ChartStyles.Color.Background)
        .setQuantityBorderColor(lineInfo.color);
      this.#lines[side]
        .setText(lineInfo.prefix + numberFormatter(options.pnl, { toFixed: true, precision: 2 }))
        .setQuantity(options.qty)
        .setPrice(options.price);
    } catch {
      // Value is null error
    }
  }

  clean(side: 'long' | 'short') {
    this.#lines[side]?.remove();
    this.#lines[side] = null;
  }
}

function calculatePositions(positions: OpenPosition[]): Record<'long' | 'short', DrawOptions> {
  const sideValues = {
    Long: {
      totalQty: Num(0),
      weighted: Num(0),
      pnl: Num(0),
    },
    Short: {
      totalQty: Num(0),
      weighted: Num(0),
      pnl: Num(0),
    },
  };

  for (const pos of positions) {
    const qty = Num(pos.quantity);
    sideValues[pos.side].totalQty = sideValues[pos.side].totalQty.plus(qty);
    sideValues[pos.side].weighted = sideValues[pos.side].weighted.plus(qty.multipliedBy(pos.avgEntryPrice));
    sideValues[pos.side].pnl = sideValues[pos.side].pnl.plus(pos.unrealizedPnl);
  }

  const pnl = (side: OpenPosition['side']) => {
    const sideValue = sideValues[side];
    return `${sideValue.pnl.isPositive() ? '+' : ''}${numberFormatter(sideValue.pnl.toFixed(UsdtPrecision))}`;
  };

  const totalQty = (side: OpenPosition['side']) => numberFormatter(sideValues[side].totalQty.abs().toString());

  const calcBEP = (side: OpenPosition['side']) => {
    const sideValue = sideValues[side];
    return sideValue.totalQty.isZero() ? 0 : sideValue.weighted.dividedBy(sideValue.totalQty).toNumber();
  };

  return {
    long: {
      pnl: pnl('Long'),
      qty: totalQty('Long'),
      price: calcBEP('Long'),
    },
    short: {
      pnl: pnl('Short'),
      qty: totalQty('Short'),
      price: calcBEP('Short'),
    },
  };
}
